import React, { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { Stack, Chip, Grid, TextField, InputLabel, Box } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Threshold } from '../../services/api';

export enum ThresholdMode {
  minValue = 'minValue',
  maxValue = 'maxValue',
  minRange = 'minRange',
  maxRange = 'maxRange',
}

export const THRESHOLD_MODES = [
  { value: ThresholdMode.minValue, label: 'Min Value' },
  { value: ThresholdMode.maxValue, label: 'Max Value' },
  { value: ThresholdMode.minRange, label: 'Min Range' },
  { value: ThresholdMode.maxRange, label: 'Max Range' },
];

interface ThresholdProps {
  isHistoryType: boolean;
  value: Threshold;
  onChange: (threshold: Threshold) => void;
  allowEditing: boolean;
}

function ThresholdSection({ isHistoryType, value, onChange, allowEditing }: ThresholdProps) {
  const theme = useTheme();

  // initilize whatever mode is already present in value
  const [thresholdMode, setThresholdMode] = useState<ThresholdMode>(() => {
    if (value.minValue !== undefined) return ThresholdMode.minValue;
    if (value.maxValue !== undefined) return ThresholdMode.maxValue;
    if (value.maxRange !== undefined) return ThresholdMode.maxRange;
    if (value.minRange !== undefined) return ThresholdMode.minRange;
    return ThresholdMode.maxValue;
  });

  return (
    <Box>
      <Box sx={{ display: { xs: 'block', sm: 'flex' } }}>
        <Tooltip title="Input marginal value for the alert" placement="top" arrow>
          <InputLabel sx={{ mb: 2, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            Select Threshold <HelpOutlineIcon sx={{ fontSize: '1rem', ml: 1 }} />
          </InputLabel>
        </Tooltip>

        <Stack direction="row" spacing={1} sx={{ mb: 2, ml: 2, flexWrap: 'wrap', gap: 1 }}>
          {THRESHOLD_MODES.filter(
            (m) =>
              isHistoryType ||
              (m.value !== ThresholdMode.minRange && m.value !== ThresholdMode.maxRange)
          ).map((m) => (
            <Chip
              key={m.value}
              label={m.label}
              variant={thresholdMode === m.value ? 'outlined' : 'filled'}
              color={thresholdMode === m.value ? 'success' : 'default'}
              onClick={() => setThresholdMode(m.value)}
              size="small"
              disabled={!allowEditing}
            />
          ))}
        </Stack>
      </Box>
      <Grid container spacing={1.5}>
        <Grid size={{ sm: 6, xs: 12 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder="e.g. 0"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            value={(value as Threshold)[thresholdMode] ?? ''}
            onChange={(e) => {
              const newValue = e.target.value;
              onChange({
                ...(value as Threshold),
                [thresholdMode]: newValue === '' ? undefined : Number(newValue),
              });
            }}
            variant="outlined"
            sx={{ border: `solid 1px ${theme.palette.primary.contrastText}` }}
            disabled={!allowEditing}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default ThresholdSection;
